import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon';
import ResizeImage from '@/components/atoms/ResizeImage';
import updateDataLayer from '@/utils/gtml';
import * as React from 'react';
import { FocusScope, useFocusManager, useKeyboard, usePreventScroll } from 'react-aria';
import { twMerge } from 'tailwind-merge';
import HeaderContent from '../HeaderContent';
import HeaderNavLinks from '../HeaderNavLinks';
const HeaderMobileTabMenu = ({ activeItem, onChangeActiveItemChild }) => {
    const focusManager = useFocusManager();
    const onMenuItemKeyUp = (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            focusManager?.focusPrevious();
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            focusManager?.focusNext();
        }
    };
    return (_jsx("div", { className: "flex items-stretch border-b border-border-brand", children: [activeItem.item.children.left, activeItem.item.children.right].map((child) => (_jsx("button", { onKeyUp: (event) => onMenuItemKeyUp(event), className: twMerge('type-feature-body relative h-4.5 w-full px-1.5', activeItem.selectedChild === child
                ? "italic text-border-brand after:absolute after:bottom-0 after:left-0 after:right-0 after:block after:h-[3px] after:bg-border-brand after:content-['']"
                : 'text-text-body'), onClick: () => onChangeActiveItemChild(child), children: child.title }, child.title))) }));
};
const HeaderMobile = ({ logo, navigationCTA, contactCTA, navigation, backLabel, closeLabel, openLabel, closeIcon, openIcon, isScrolled, }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    usePreventScroll({ isDisabled: !isOpen });
    useKeyboard({
        onKeyUp: (event) => {
            if (event.key === 'Escape') {
                onCloseActiveItem();
            }
        },
    });
    const refItemScrollContainer = React.useRef(null);
    const [items, setItems] = React.useState(navigation
        .filter((item) => item.children?.left || item.children?.right)
        .map((item) => {
        const hasNoCards = ![item.children?.left, item.children?.right].some((child) => child?.reactComponentName === 'Components.NavCard');
        const hasTwoChildren = !!item.children?.left && !!item.children?.right;
        const canSelectChildren = hasNoCards && hasTwoChildren;
        const selectedChild = canSelectChildren
            ? [item.children?.left, item.children?.right].filter(Boolean).at(0)
            : undefined;
        return { item, canSelectChildren, selectedChild, isActive: false };
    }));
    const onOpenActiveItem = (event, selectedItem) => {
        if (!selectedItem.children?.left && !selectedItem.children?.right) {
            updateDataLayer({
                event: 'menuUsed',
                menu_type: 'Header',
                main_category: selectedItem.text,
            });
            return;
        }
        event.preventDefault();
        setItems((items) => items.map((item) => item.item === selectedItem
            ? {
                ...item,
                isActive: true,
            }
            : item));
    };
    const onChangeActiveItemChild = (child) => {
        refItemScrollContainer.current?.scrollTo(0, 0);
        setItems((items) => items.map((item) => item.isActive
            ? {
                ...item,
                selectedChild: child,
            }
            : item));
    };
    const onCloseActiveItem = () => {
        setItems((items) => items.map((item) => item.isActive
            ? {
                ...item,
                isActive: false,
                selectedChild: item.canSelectChildren ? item.item.children?.left : undefined,
            }
            : item));
    };
    const onToggleOpen = () => {
        setIsOpen(!isOpen);
        if (isOpen) {
            setTimeout(() => {
                onCloseActiveItem();
            }, 500);
        }
    };
    const renderContactCTA = (notVisible) => (_jsx("div", { className: "p-1.5", "data-react-aria-prevent-focus": notVisible ? '' : undefined, children: _jsx(Button, { variant: "text-only", icon: contactCTA.linkIcon, mode: "interactive", width: "auto", children: (props) => (_jsxs("a", { ...props, href: contactCTA.url, target: contactCTA.target, onClick: () => updateDataLayer({
                    event: 'ctaClick',
                    cta_type: contactCTA.linkDisplay,
                    cta_text: contactCTA.text,
                    cta_url: contactCTA.url,
                }), className: twMerge(props.className, '!border-b-0 !pb-0'), children: [contactCTA.text, _jsx(Icon, { variant: props.icon, className: "h-1.25 w-1.25" })] })) }) }));
    const hasActiveItem = items.some(({ isActive }) => isActive);
    return (_jsxs(FocusScope, { contain: isOpen, children: [_jsxs("div", { className: twMerge('relative flex h-header-row items-center justify-center border-b border-border-brand px-1.5', isScrolled ? 'backdrop-frosted bg-surface-frosted/70' : 'bg-surface-primary', isOpen && '!bg-surface-primary'), children: [_jsx("button", { className: "absolute left-1.25 text-text-body", onClick: () => onToggleOpen(), "aria-label": isOpen ? closeLabel : openLabel, children: _jsx(Icon, { variant: isOpen ? closeIcon : openIcon, className: "h-2 w-2" }) }), _jsx("a", { href: logo.url, className: "h-auto w-[35%] max-w-12 xs:h-2.5 xs:w-1/2", children: _jsx(ResizeImage, { loading: "eager", positioning: "none", className: "h-full w-full text-surface-brand", image: logo.image }) }), _jsx(Button, { variant: navigationCTA.linkDisplay, size: "small", icon: navigationCTA.linkIcon, mode: "interactive", width: "auto", children: (props) => (_jsxs("a", { ...props, href: navigationCTA.url, target: navigationCTA.target, className: twMerge('absolute right-0.75 !w-auto xs:right-1.5', props.className), onClick: () => updateDataLayer({
                                event: 'ctaClick',
                                cta_type: navigationCTA.linkDisplay,
                                click_text: navigationCTA.text,
                                click_url: navigationCTA.url,
                            }), children: [navigationCTA.text, _jsx(Icon, { variant: props.icon, className: "h-1.25 w-1.25" })] })) })] }), _jsx("div", { inert: !isOpen ? '' : undefined, className: "fixed bottom-0 top-4.5 w-full overflow-hidden", children: _jsxs("div", { className: twMerge('absolute left-0 right-0 top-0 flex h-[calc(100%+1px)] w-full flex-col border-b border-border-brand bg-surface-primary transition-transform duration-500', !isOpen && '-translate-y-full'), children: [_jsx("div", { className: "custom-scrollbar grow overflow-y-auto p-1.5 pt-0", "data-react-aria-prevent-focus": hasActiveItem ? '' : undefined, children: _jsx(HeaderNavLinks, { sections: [{ links: navigation }], linkHeight: "lg", onLinkClick: (event, item) => onOpenActiveItem(event, item) }) }), renderContactCTA(hasActiveItem), items.map(({ item, canSelectChildren, selectedChild, isActive }) => {
                            return (_jsxs("div", { className: twMerge('absolute flex h-full w-[calc(100%+1px)] translate-x-[-1px] flex-col border-l border-border-brand bg-surface-primary transition-transform duration-500', !isActive && 'translate-x-full'), inert: isActive ? undefined : '', "data-react-aria-prevent-focus": isActive ? undefined : '', children: [_jsx("div", { className: "border-b border-border-brand", children: _jsxs("button", { className: "flex items-center gap-0.25 p-1.5 text-s-button uppercase text-text-body", onClick: () => onCloseActiveItem(), children: [_jsx(Icon, { variant: "chevron-left", className: "h-1.25 w-1.25" }), backLabel] }) }), canSelectChildren && (_jsx(HeaderMobileTabMenu, { activeItem: { item, selectedChild }, onChangeActiveItemChild: onChangeActiveItemChild })), _jsxs("div", { ref: refItemScrollContainer, className: "custom-scrollbar grow overflow-y-auto", children: [_jsx(HeaderContent, { showTitles: !canSelectChildren, content: canSelectChildren ? [selectedChild] : [item.children?.left, item.children?.right], onLinkClick: (_, childItem) => updateDataLayer({
                                                    event: 'menuUsed',
                                                    menu_type: 'Header',
                                                    main_category: item.text,
                                                    sub_category: childItem.text,
                                                }) }), renderContactCTA()] }, selectedChild?.title)] }, item.text));
                        })] }) })] }));
};
export default HeaderMobile;
